<template>
  <div class="container py-5 ticket-page">
    <header v-if="!loading && ticket._id" class="ticket-page__header">
      <div class="ticket-page__brand">
        <img v-if="info && info.logo" :src="getLogo(info.logo)" :alt="info.custom_name || info.name" />
        <span v-else class="ticket-page__brand-icon" aria-hidden="true"><b-icon icon="bag-check" /></span>
        <div>
          <small>ESTADO DE TU PEDIDO</small>
          <strong>{{ (info && (info.custom_name || info.name)) || "Tu pedido" }}</strong>
        </div>
      </div>
      <button type="button" class="ticket-page__new-order" @click="goCart()">
        <b-icon icon="bag-plus" aria-hidden="true" />
        Hacer nuevo pedido
      </button>
    </header>
    <div class="row" v-if="loading">
      <div class="col-12 col-md-8 mx-auto text-center mt-4 py-5">
        <div class="ticket-loading">
          <div class="ticket-loading__logo-wrap" v-if="info && info.logo">
            <img
              :src="getLogo(info.logo)"
              :alt="info.custom_name || info.name"
              class="ticket-loading__logo"
            />
          </div>
          <div
            class="spinner-border text-success ticket-loading__spinner"
            role="status"
          >
            <span class="sr-only">Loading...</span>
          </div>
          <small class="ticket-loading__eyebrow">
            {{
              (info && (info.custom_name || info.name)) ||
              "Preparando tu pedido"
            }}
          </small>
          <h4 class="mt-3 mb-2">Cargando detalles de tu pedido</h4>
          <p class="text-muted mb-0">
            Estamos consultando estado, productos y datos del local.
          </p>
          <button
            class="mt-4 btn btn-outline-danger"
            @click="load"
            v-if="tid && showReload"
          >
            Reintentar carga
          </button>
        </div>
      </div>
    </div>
    <div class="row justify-content-center" v-if="!loading && ticket._id">
      <div class="col-12 col-xl-11">
        <section class="ticket-status" :class="`ticket-status--${statusTone}`" role="status" aria-live="polite">
          <div class="ticket-status__topline">
            <span>SEGUIMIENTO EN TIEMPO REAL</span>
            <strong>#{{ ticket.number | formatNumber }}</strong>
          </div>
          <div class="ticket-status__main">
            <span class="ticket-status__icon" aria-hidden="true"><b-icon :icon="statusIcon" /></span>
            <div class="ticket-status__copy">
              <small>{{ statusLabel }}</small>
              <h1>{{ statusTitle }}</h1>
              <p>{{ statusDescription }}</p>
            </div>
          </div>
          <div v-if="ticket.status === 'Cancelado' && ticket.reason" class="ticket-status__detail">
            <strong>Motivo:</strong> {{ ticket.reason }}
          </div>
          <div v-if="ticket.status === 'Aceptado' && ticket.hour" class="ticket-status__detail">
            <b-icon icon="clock" aria-hidden="true" />
            Entrega aproximada: <strong>{{ getAproxHour() }}</strong>
          </div>
          <ol v-if="ticket.status !== 'Cancelado'" class="ticket-status__steps" aria-label="Progreso del pedido">
            <li
              v-for="(step, index) in statusSteps"
              :key="step"
              :class="{ 'is-complete': index < progressStep, 'is-current': index === progressStep - 1 }"
            >
              <span class="ticket-status__step-dot" aria-hidden="true"><b-icon :icon="index < progressStep ? 'check' : 'circle'" /></span>
              {{ step }}
            </li>
          </ol>
          <div v-if="ticket.status === 'Pendiente'" class="ticket-status__footnote">
            <span><span class="ticket-status__live-dot" aria-hidden="true"></span> Revisamos el estado cada minuto.</span>
            <span v-if="ticket.email">También te avisaremos por correo cuando cambie.</span>
            <button v-if="showReload" type="button" @click="load">Actualizar ahora</button>
          </div>
        </section>
        <div class="row justify-content-center ticket-details" v-if="info">
          <div class="col">
            <div class="row" v-if="loading == false">
              <div
                class="col-12 mb-4"
                v-if="calificationAvailable(ticket) && isQuery()"
              >
                <div class="row ticket-panel ticket-rating">
                  <div class="col-12 mb-2">
                    <strong class="d-block">Califica tu pedido</strong>
                    <small class="text-muted">
                      Tu opinión ayuda a mejorar próximos pedidos.
                    </small>
                  </div>
                  <div class="col-12 mb-3">
                    <b-form-rating
                      v-model="rating"
                      variant="warning"
                      stars="5"
                      show-value
                      no-border
                      :disabled="ratingLoading || ratingSaved"
                    ></b-form-rating>
                  </div>
                  <div class="col-12">
                    <small class="d-block mb-2">COMENTARIO (OPCIONAL)</small>
                    <div class="input-group align-items-stretch">
                      <textarea
                        class="form-control"
                        cols="10"
                        rows="3"
                        v-model="comment"
                        :disabled="ratingLoading || ratingSaved"
                        placeholder="Cuéntanos como fue tu experiencia"
                      ></textarea>
                      <div class="input-group-append">
                        <button
                          class="btn btn-success h-100 px-4 font-weight-bold"
                          :disabled="
                            ratingLoading || ratingSaved || !canSubmitRating
                          "
                          @click="saveRating()"
                        >
                          <span v-if="ratingLoading">
                            <span
                              class="spinner-border spinner-border-sm mr-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Enviando...
                          </span>
                          <span v-else-if="ratingSaved">Enviado</span>
                          <span v-else>ENVIAR</span>
                        </button>
                      </div>
                    </div>
                    <small class="text-success d-block mt-2" v-if="ratingSaved">
                      Gracias, tu calificacion fue enviada correctamente.
                    </small>
                  </div>
                </div>
              </div>
              <div class="col-12 col-lg-7">
                <div class="ticket-panel ticket-summary">
                  <div class="ticket-summary__eyebrow">
                    Pedido {{ ticket.type.toLowerCase() }}
                  </div>
                  <h4 class="ticket-summary__title">
                    #{{ ticket.number | formatNumber }}
                  </h4>
                  <div v-if="info" class="ticket-summary__store">
                    <strong class="d-block">{{
                      info.custom_name || info.name
                    }}</strong>
                    <span class="text-muted"
                      >{{ info.address }}, {{ info.city }}</span
                    >
                  </div>
                  <div class="ticket-meta">
                    <div class="ticket-meta__item">
                      <small>Cliente</small>
                      <strong>{{ ticket.clientName }}</strong>
                    </div>
                    <div class="ticket-meta__item">
                      <small>Telefono</small>
                      <strong>{{ ticket.phone }}</strong>
                    </div>
                    <div class="ticket-meta__item">
                      <small>Fecha</small>
                      <strong>{{
                        ticket.createdAt | moment("DD/MM/YY HH:mm")
                      }}</strong>
                    </div>
                    <div
                      class="ticket-meta__item"
                      v-if="ticket.type == 'Domicilio'"
                    >
                      <small>Direccion</small>
                      <strong>{{ ticket.address }}</strong>
                    </div>
                    <div class="ticket-meta__item" v-if="ticket.date">
                      <small>Programado</small>
                      <strong>{{
                        formatScheduledTicketDate(ticket.date, ticket.time)
                      }}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-12 col-lg-5">
                <div class="ticket-panel ticket-help mt-3 mt-lg-0">
                  <small class="ticket-panel__label">Ayuda con tu pedido</small>
                  <p class="mb-3">
                    Si tienes dudas o quieres cancelar tu pedido debes
                    comunicarte con el local.
                  </p>
                  <a
                    v-if="info.phone"
                    class="btn btn-outline-warning btn-block font-weight-bold"
                    :href="`tel:${info.phone}`"
                  >
                    <b-icon icon="phone" class="mr-2"></b-icon>
                    Llamar {{ info.phone }}
                  </a>
                  <button
                    class="btn btn-success btn-block mt-2"
                    @click="sendToWhatsApp()"
                    v-if="settings.whatsapp"
                  >
                    <b-icon icon="whatsapp" class="mr-2"></b-icon>
                    Enviar por Whatsapp
                  </button>
                </div>
              </div>

              <div
                class="ml-auto col-md-6 col-12 mt-3 d-none"
                v-if="
                  ticket.status == 'Aceptado' &&
                  ticket.payment == 'Transferencia' &&
                  false
                "
              >
                <div class="alert alert-info">
                  <h4>
                    Datos para transferencia por ${{
                      formatNumber(ticket.total)
                    }}
                  </h4>
                  <pre>{{ ticket.bankAccount }}</pre>
                </div>
              </div>

              <div
                class="col-md-12 col mt-3"
                v-if="ticket.status == 'Aceptado' && payment.details"
              >
                <div class="ticket-panel ticket-panel--info">
                  <small class="ticket-panel__label">Medio de pago</small>
                  <h5 class="text-uppercase mb-2">
                    Detalles para {{ payment.name }}
                  </h5>
                  <div v-html="getContent(payment.details)"></div>
                </div>
              </div>

              <div class="col-md-12 col mt-3" v-if="ticket.notes">
                <div class="ticket-panel ticket-panel--notes">
                  <small class="ticket-panel__label">Notas del pedido</small>
                  <div v-html="getContent(ticket.notes)"></div>
                </div>
              </div>

              <div class="col-12 mt-3">
                <div
                  class="ticket-paid"
                  v-if="ticket.paymentStatus == 'ok'"
                >
                  <b-icon icon="check-circle" aria-hidden="true" />
                  <span>Pago en línea confirmado</span>
                </div>
              </div>

              <div
                class="col-12 mt-4"
                v-if="ticket.flowOrders && ticket.flowOrders.length > 0"
              >
                <div class="row">
                  <div class="col-12">
                    <small class="text-muted">Pagos en linea</small>
                  </div>
                </div>
                <div
                  class="list-group-item"
                  v-for="event in ticket.flowOrders"
                  :key="event._id"
                >
                  <div class="row">
                    <div class="col-4 col-md-2">
                      <strong>{{ event.createdAt | moment("HH:mm") }}</strong>
                    </div>
                    <div class="col">
                      {{ event.status == "ok" ? "Pagado" : "No Pagado" }}
                    </div>
                    <div class="col">#{{ event.order }}</div>
                    <div class="col text-right">
                      <strong>${{ event.total | formatNumber }}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-12 mt-4">
                <div class="ticket-panel ticket-order">
                  <div class="ticket-order__header">
                    <small class="ticket-panel__label mb-0"
                      >Resumen del pedido</small
                    >
                    <strong
                      >{{ ticket.products.length }} producto<span
                        v-if="ticket.products.length !== 1"
                        >s</span
                      ></strong
                    >
                  </div>
                  <div
                    class="ticket-item"
                    v-for="p in ticket.products"
                    :key="p._id"
                  >
                    <div class="ticket-item__row">
                      <div class="ticket-item__info">
                        <div class="ticket-item__title">
                          <span class="ticket-item__qty">{{ p.qty }} x</span>
                          <span class="font-weight-bold text-uppercase">{{
                            p.name
                          }}</span>
                        </div>
                        <small class="d-block text-muted">
                          Valor unitario ${{ formatNumber(p.value) }}
                        </small>
                        <span class="ticket-item__note">{{
                          p.details || "Sin notas"
                        }}</span>
                      </div>
                      <div class="ticket-item__total">
                        ${{ formatNumber(p.total) }}
                      </div>
                    </div>
                    <div
                      class="ticket-options"
                      v-if="p.options && p.options.length > 0"
                    >
                      <div class="ticket-options__label">Opciones</div>
                      <div
                        class="ticket-option"
                        v-for="op in p.options"
                        :key="op._id"
                      >
                        <strong>{{ op.label || op.name }}:</strong>
                        <span v-if="op.values && op.values.length > 0">{{
                          op.values.map((o) => o.name).join(", ")
                        }}</span>
                        <span v-else>{{ op.selected }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="ticket-totals">
                    <div class="ticket-totals__row">
                      <span>Medio de pago</span>
                      <strong>{{ ticket.payment }}</strong>
                    </div>
                    <div class="ticket-totals__row">
                      <span>Sub-total</span>
                      <strong>${{ formatNumber(sub_total) }}</strong>
                    </div>
                    <div class="ticket-totals__row" v-if="ticket.discount">
                      <span>Descuento</span>
                      <strong>-${{ formatNumber(ticket.discount) }}</strong>
                    </div>
                    <div
                      class="ticket-totals__row"
                      v-if="Number(ticket.deliveryValue) > 0"
                    >
                      <span>Envio</span>
                      <strong>${{ formatNumber(ticket.deliveryValue) }}</strong>
                    </div>
                    <div class="ticket-totals__row ticket-totals__row--grand">
                      <span>Total</span>
                      <strong>${{ formatNumber(ticket.total) }}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div
                class="payment-buttons"
                v-if="!loading && showPaymentButton"
              >
                <button
                  class="btn btn-success mx-auto px-5 btn-lg"
                  v-if="
                    ticket.paymentStatus != 'ok' &&
                    ticket.payment == 'Webpay' &&
                    ticket.status != 'Cancelado'
                  "
                  @click="payTicket()"
                  :disabled="loading"
                >
                  PAGAR POR WEBPAY
                </button>
                <button
                  class="btn btn-success mx-auto px-5 btn-lg"
                  v-if="
                    ticket.paymentStatus != 'ok' &&
                    ticket.payment == 'Klap' &&
                    ticket.status != 'Cancelado'
                  "
                  @click="payTicketKlap()"
                  :disabled="loading"
                >
                  PAGAR POR KLAP
                </button>
                <button
                  class="btn btn-success mx-auto px-5 btn-lg"
                  v-if="
                    ticket.paymentStatus != 'ok' &&
                    ticket.payment == 'MercadoPago' &&
                    ticket.status != 'Cancelado'
                  "
                  @click="payTicketMercadoPago()"
                  :disabled="loading"
                >
                  PAGAR POR MERCADOPAGO
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      ref="modal"
      title="Cargando..."
      no-close-on-backdrop
      hide-header
      hide-footer
    >
      <div class="row py-5">
        <div class="col text-center">
          <b-spinner
            variant="success"
            size="xl"
            type="grow"
            label="Spinning"
          ></b-spinner>
          <div class="w-100 py-2"></div>
          Cargando Pago...
        </div>
      </div>
    </b-modal>
  </div>
</template>

<style scoped>
.ticket-page {
  --ticket-ink: #243b53;
  --ticket-muted: #64758b;
  max-width: 1200px;
  padding-top: clamp(20px, 4vw, 48px) !important;
  padding-bottom: 3rem;
  color: var(--ticket-ink);
}

.ticket-page__header {
  display: flex;
  max-width: 1045px;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin: 0 auto 22px;
}

.ticket-page__brand {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
}

.ticket-page__brand img,
.ticket-page__brand-icon {
  display: grid;
  width: 54px;
  height: 54px;
  flex: 0 0 54px;
  place-items: center;
  padding: 7px;
  border: 1px solid #dce6ef;
  border-radius: 16px;
  background: #fff;
  object-fit: contain;
  box-shadow: 0 8px 20px rgba(26, 55, 83, 0.07);
}

.ticket-page__brand-icon {
  color: #2f83c5;
  font-size: 1.55rem;
}

.ticket-page__brand small,
.ticket-page__brand strong {
  display: block;
}

.ticket-page__brand small {
  color: #6e8196;
  font-size: 0.67rem;
  font-weight: 800;
  letter-spacing: 0.13em;
}

.ticket-page__brand strong {
  overflow: hidden;
  font-size: 1.05rem;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ticket-page__new-order {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 10px 16px;
  border: 1px solid #ccdae8;
  border-radius: 12px;
  background: #fff;
  color: #315a7b;
  font-size: 0.87rem;
  font-weight: 800;
  box-shadow: 0 5px 16px rgba(31, 65, 96, 0.05);
  transition: background-color 180ms ease, transform 180ms ease;
}

.ticket-page__new-order:hover,
.ticket-page__new-order:focus-visible {
  background: #eff7ff;
  color: #1a6ea9;
  transform: translateY(-2px);
}

.ticket-status {
  --status-accent: #287ebd;
  --status-soft: #dff0fc;
  --status-wash: rgba(80, 175, 235, 0.16);
  overflow: hidden;
  padding: clamp(22px, 3.5vw, 38px);
  border: 1px solid #d5e6f2;
  border-radius: 26px;
  background: radial-gradient(circle at 92% 6%, var(--status-wash), transparent 38%), linear-gradient(140deg, #fff 38%, #f3f9fe);
  box-shadow: 0 18px 44px rgba(31, 66, 99, 0.09);
}

.ticket-status--accepted {
  --status-accent: #19835f;
  --status-soft: #dff6eb;
  --status-wash: rgba(75, 194, 142, 0.16);
  border-color: #cfe9da;
}

.ticket-status--cancelled {
  --status-accent: #ba4e4e;
  --status-soft: #ffeded;
  --status-wash: rgba(239, 113, 113, 0.13);
  border-color: #f2d8d8;
}

.ticket-status__topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  color: var(--status-accent);
  font-size: 0.69rem;
  font-weight: 850;
  letter-spacing: 0.14em;
}

.ticket-status__topline strong {
  padding: 7px 11px;
  border: 1px solid currentColor;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.65);
  font-size: 0.76rem;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.ticket-status__main {
  display: flex;
  align-items: center;
  gap: clamp(16px, 2.5vw, 28px);
  margin-top: 22px;
}

.ticket-status__icon {
  display: grid;
  width: 76px;
  height: 76px;
  flex: 0 0 76px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 22px;
  background: var(--status-soft);
  color: var(--status-accent);
  font-size: 2rem;
}

.ticket-status__copy small {
  color: var(--status-accent);
  font-size: 0.7rem;
  font-weight: 850;
  letter-spacing: 0.12em;
}

.ticket-status__copy h1 {
  margin: 5px 0 7px;
  color: var(--ticket-ink);
  font-size: clamp(1.6rem, 3vw, 2.3rem);
  font-weight: 850;
  letter-spacing: -0.045em;
  line-height: 1.1;
}

.ticket-status__copy p {
  margin: 0;
  color: var(--ticket-muted);
  font-size: 0.95rem;
  line-height: 1.5;
}

.ticket-status__detail {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 22px;
  padding: 10px 14px;
  border-radius: 10px;
  background: var(--status-soft);
  color: var(--ticket-ink);
  font-size: 0.9rem;
}

.ticket-status__steps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin: 26px 0 0;
  padding: 0;
  list-style: none;
}

.ticket-status__steps li {
  display: flex;
  min-height: 54px;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border: 1px solid #e2eaf0;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.76);
  color: #7a8a9b;
  font-size: 0.83rem;
  font-weight: 750;
}

.ticket-status__steps li.is-complete {
  border-color: #c9e4f3;
  color: #365a73;
}

.ticket-status__steps li.is-current {
  border-color: var(--status-accent);
  background: var(--status-soft);
  color: var(--status-accent);
}

.ticket-status__step-dot {
  display: grid;
  width: 25px;
  height: 25px;
  flex: 0 0 25px;
  place-items: center;
  border-radius: 50%;
  background: #e9eff4;
}

.ticket-status__steps li.is-complete .ticket-status__step-dot {
  background: var(--status-accent);
  color: #fff;
}

.ticket-status__footnote {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 20px;
  margin-top: 17px;
  color: #607a91;
  font-size: 0.8rem;
}

.ticket-status__footnote button {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--status-accent);
  font-weight: 800;
  text-decoration: underline;
}

.ticket-status__live-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 5px;
  border-radius: 50%;
  background: #23a36f;
  box-shadow: 0 0 0 4px rgba(35, 163, 111, 0.13);
}

.ticket-details {
  margin-top: 24px;
}

.ticket-rating {
  margin: 0;
  padding-top: 19px;
  padding-bottom: 19px;
}

.ticket-paid {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 13px 16px;
  border: 1px solid #bde7ce;
  border-radius: 12px;
  background: #ecfbf2;
  color: #16734f;
  font-weight: 800;
}
.payment-buttons {
  position: sticky;
  bottom: 0px;
  z-index: 40;
  width: 100%;
  max-width: 680px;
  margin: 20px auto 1.5rem;
  padding: 0.75rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.18);
  backdrop-filter: blur(10px);
}
.payment-buttons .btn {
  width: 100%;
  display: block;
  margin: 0 auto;
  min-height: 54px;
  border-radius: 12px;
  white-space: normal;
  line-height: 1.15;
}
.ticket-primary-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 58px;
  border: 0;
  border-radius: 14px;
  background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
  box-shadow: 0 14px 30px rgba(34, 197, 94, 0.22);
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
}

.ticket-primary-cta:hover,
.ticket-primary-cta:focus {
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 18px 34px rgba(34, 197, 94, 0.28);
  filter: saturate(1.05);
}

.ticket-primary-cta:active {
  transform: translateY(0);
  box-shadow: 0 10px 22px rgba(34, 197, 94, 0.2);
}

.ticket-loading {
  padding: 2rem 1.5rem;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #dbe3ef;
  border-radius: 20px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.06);
}

.ticket-loading__logo-wrap {
  width: 112px;
  height: 112px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
  padding: 1rem;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 16px 30px rgba(15, 23, 42, 0.08);
  animation: ticket-loading-float 2.4s ease-in-out infinite;
}

.ticket-loading__logo {
  max-width: 100%;
  max-height: 64px;
  object-fit: contain;
}

.ticket-loading__spinner {
  width: 2.75rem;
  height: 2.75rem;
}

.ticket-loading__eyebrow {
  display: block;
  margin-top: 1rem;
  color: #16a34a;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.ticket-panel {
  background: #fff;
  border: 1px solid #dbe3ef;
  border-radius: 18px;
  padding: clamp(1.2rem, 2.4vw, 1.75rem);
  box-shadow: 0 12px 32px rgba(31, 60, 88, 0.06);
}

.ticket-panel__label {
  display: block;
  margin-bottom: 0.5rem;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.ticket-summary__eyebrow {
  color: #2c83c2;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.ticket-summary__title {
  margin: 0.15rem 0 0.75rem;
  color: #223a51;
  font-size: 2.2rem;
  font-weight: 800;
  letter-spacing: -0.04em;
}

.ticket-summary__store {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.ticket-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
}

.ticket-meta__item {
  min-width: 0;
  padding: 0.8rem 0.9rem;
  border-radius: 11px;
  background: #f6f9fc;
}

.ticket-meta__item small {
  display: block;
  margin-bottom: 0.15rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.ticket-meta__item strong {
  color: #334155;
  font-weight: 700;
}

.ticket-help {
  height: 100%;
  background: linear-gradient(160deg, #fff8e4 0%, #fffdf5 100%);
  border-color: #f1dfb2;
}

.ticket-help .btn {
  min-height: 43px;
  border-radius: 10px;
}

.ticket-help .btn-outline-warning {
  border-color: #bd9650;
  color: #77551c;
}

.ticket-help .btn-outline-warning:hover {
  background: #77551c;
  color: #fff;
}

.ticket-panel--info {
  background: #eef8ff;
  border-color: #bfe4fb;
}

.ticket-panel--notes {
  background: #f8fafc;
}

.ticket-order__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.ticket-order__header strong {
  color: #2d5f87;
  font-size: 0.85rem;
}

.ticket-item {
  padding: 1rem 0;
  border-top: 1px solid #edf2f7;
}

.ticket-item:first-of-type {
  border-top: 0;
  padding-top: 0;
}

.ticket-item__row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.ticket-item__title {
  margin-bottom: 0.2rem;
  color: #334155;
  font-size: 1rem;
}

.ticket-item__qty {
  margin-right: 0.35rem;
  color: #64748b;
  font-weight: 700;
}

.ticket-item__note {
  display: block;
  margin-top: 0.4rem;
  color: #475569;
}

.ticket-item__total {
  color: #0f172a;
  font-size: 1.1rem;
  font-weight: 800;
  white-space: nowrap;
}

.ticket-options {
  margin-top: 0.85rem;
  padding: 0.85rem 1rem;
  background: #f8fafc;
  border-radius: 10px;
}

.ticket-options__label {
  margin-bottom: 0.5rem;
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
}

.ticket-option {
  margin-top: 0.25rem;
  color: #334155;
}

.ticket-option strong {
  margin-right: 0.35rem;
}

.ticket-totals {
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #dbe3ef;
}

.ticket-totals__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.45rem 0;
  color: #334155;
}

.ticket-totals__row--grand {
  margin-top: 0.3rem;
  padding-top: 0.9rem;
  border-top: 1px solid #e5e7eb;
  color: #0f172a;
  font-size: 1.1rem;
}

@media (max-width: 767px) {
  .ticket-page__header {
    align-items: flex-start;
  }

  .ticket-page__new-order {
    flex: 0 0 auto;
    font-size: 0.8rem;
  }

  .ticket-status__icon {
    width: 58px;
    height: 58px;
    flex-basis: 58px;
    border-radius: 17px;
    font-size: 1.55rem;
  }

  .ticket-summary__title {
    font-size: 1.6rem;
  }

  .ticket-loading {
    padding: 1.5rem 1rem;
  }

  .ticket-loading__logo-wrap {
    width: 92px;
    height: 92px;
  }

  .ticket-item__row,
  .ticket-totals__row {
    flex-direction: column;
    align-items: flex-start;
  }

  .ticket-item__total {
    margin-top: 0.35rem;
  }
}

@media (max-width: 575.98px) {
  .ticket-page__header {
    flex-direction: column;
  }

  .ticket-page__new-order {
    width: 100%;
  }

  .ticket-status {
    padding: 19px 16px;
    border-radius: 19px;
  }

  .ticket-status__main {
    align-items: flex-start;
    gap: 12px;
  }

  .ticket-status__copy h1 {
    font-size: 1.5rem;
  }

  .ticket-status__steps {
    gap: 5px;
  }

  .ticket-status__steps li {
    min-height: 62px;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    padding: 7px 3px;
    font-size: 0.68rem;
    text-align: center;
  }

  .ticket-meta {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ticket-page__new-order {
    transition: none;
  }
}

@keyframes ticket-loading-float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-6px);
  }
}
</style>

<script>
import { redirectGatewayPayment } from "../utils/publicGateway";

export default {
  data() {
    return {
      showReload: false,
      comment: "",
      rating: 0,
      ratingLoading: false,
      ratingSaved: false,
      ticket: {
        business: {},
      },
      tid: 0,
      loading: true,
      refreshTimer: null,
      pageActive: true,
    };
  },
  mounted() {
    window.scroll(0, 0);
    this.tid = this.$route.params.id;
    this.load();

    this.checkPreference();

    this.$gtag.event("view_ticket", {
      event_category: "ticket",
      event_label: "User view ticket",
      value: 1,
    });
  },
  beforeDestroy() {
    this.pageActive = false;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
  },
  computed: {
    info() {
      return this.$store.state.info;
    },
    sub_total() {
      return this.ticket.products.reduce((t, a) => t + a.total, 0);
    },
    settings() {
      return this.info && this.info.settings ? this.info.settings : {};
    },
    statusTone() {
      if (this.ticket.status === "Cancelado") return "cancelled";
      if (this.ticket.status === "Aceptado") return "accepted";
      return "pending";
    },
    statusIcon() {
      if (this.ticket.status === "Cancelado") return "x-circle";
      if (this.ticket.status === "Aceptado") return "check-circle";
      return "clock";
    },
    statusLabel() {
      if (this.ticket.status === "Cancelado") return "PEDIDO NO CONFIRMADO";
      if (this.ticket.status === "Aceptado") return "PEDIDO CONFIRMADO";
      return "CONFIRMACIÓN PENDIENTE";
    },
    statusTitle() {
      if (this.ticket.status === "Cancelado") return "Tu pedido fue cancelado";
      if (this.ticket.status === "Aceptado") return "¡Tu pedido fue aceptado!";
      if (this.ticket.status === "Pendiente") return "Tu pedido está en revisión";
      return this.ticket.status || "Estado de tu pedido";
    },
    statusDescription() {
      if (this.ticket.status === "Cancelado") return "El local no pudo confirmar este pedido. Puedes comunicarte con ellos para obtener ayuda.";
      if (this.ticket.status === "Aceptado") return "El local confirmó tu pedido. Revisa los detalles y la hora estimada de entrega.";
      if (this.ticket.status === "Pendiente") return "Ya enviamos el pedido al local. Te avisaremos cuando responda.";
      return "Consulta aquí la información más reciente de tu pedido.";
    },
    statusSteps() {
      return ["Enviado", "En revisión", "Confirmado"];
    },
    progressStep() {
      return this.ticket.status === "Aceptado" ? 3 : 2;
    },
    payment() {
      const payments = Array.isArray(this.settings.payments)
        ? this.settings.payments
        : [];
      return (
        payments.find((p) => {
          return p.name == this.ticket.payment;
        }) || {}
      );
    },
    showPaymentButton() {
      return (
        this.ticket.paymentStatus != "ok" &&
        this.ticket.status != "Cancelado" &&
        ["Webpay", "Klap", "MercadoPago"].includes(this.ticket.payment)
      );
    },
    canSubmitRating() {
      return this.rating > 0 || this.comment.trim() !== "";
    },
  },
  methods: {
    normalizeScheduleTime(value) {
      if (
        value === -1 ||
        value === "-1" ||
        value === null ||
        value === undefined
      ) {
        return -1;
      }

      if (typeof value === "string" && value.includes(":")) {
        const [hour, minute] = value.split(":").map(Number);
        if (Number.isNaN(hour) || Number.isNaN(minute)) return -1;
        return hour * 60 + minute;
      }

      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) return -1;
      if (numericValue >= 0 && numericValue <= 23) return numericValue * 60;
      return numericValue;
    },
    formatScheduledTicketDate(date, time) {
      const scheduledAt = this.$moment(date);
      const minutes = this.normalizeScheduleTime(time);

      if (minutes >= 0) {
        scheduledAt.hour(Math.floor(minutes / 60)).minutes(minutes % 60);
      }

      return scheduledAt.format("dddd DD [de] MMMM [-] HH:mm");
    },
    checkPreference() {
      const { preference_id } = this.$route.query;

      if (!preference_id) return console.log("no preference");

      console.log("preference_id", preference_id);

      this.$http
        .get(`mercadopago/${preference_id}`)
        .then((res) => {
          console.log(res.data);
        })
        .catch((err) => {
          console.log(err);
        });
    },
    sendToWhatsApp() {
      let msg = `Hola, mi nombre es *${this.ticket.clientName}*\n`;
      msg += `Pedido para *${this.ticket.type}*.\n`;
      msg += `🎫 Numero de pedido: #${this.ticket.number}\n\n`;
      msg += `Mi pedido es:\n`;
      msg += "-".repeat(30) + "\n";

      this.ticket.products.forEach((p) => {
        msg += `✅ *${p.qty} x ${p.name}* | $${p.total}\n`;
        // if (p.options) {
        //   p.options.forEach((op) => {
        //     msg += `  - ${op.label}: ${op.selected}\n`;
        //   });
        // }
        if (p.details) msg += `  - Notas: ${p.details}\n`;
      });
      msg += `\n💲 *Total: $${this.ticket.total}*\n`;
      msg += `📱 Telefono: +56${this.ticket.phone}\n`;
      if (this.ticket.type != "Retiro")
        msg += `🗺️ Direccion: ${this.ticket.address}\n`;
      let date = this.$moment(this.ticket.createdAt).format("DD/MM/YY");
      msg += `📅 Fecha: ${date}\n`;
      if (this.ticket.hour) msg += `⌚ Hora: ${this.ticket.hour}\n`;
      msg += `Medio de pago: ${this.ticket.payment}\n`;
      msg += `Notas: ${this.ticket.notes || "Sin notas"}\n`;
      msg += `\n\n`;

      let phone = this.settings.whatsapp;
      window.open(
        `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(
          msg
        )}`,
        "_blank"
      );
    },
    isQuery() {
      let token = this.ticket.createdAt.slice(-4);
      if (token == this.$route.query.s) return true;
      return false;
    },
    saveRating() {
      if (!this.canSubmitRating || this.ratingLoading || this.ratingSaved)
        return;

      this.ratingLoading = true;
      let tid = this.ticket._id;
      this.$http
        .post(`account/ticket/${tid}/rating`, {
          note: this.rating,
          comment: this.comment,
        })
        .then(() => {
          this.ratingSaved = true;
          this.ticket.rating = {
            note: this.rating,
            comment: this.comment,
          };
        })
        .catch(() => {
          this.$swal({
            title: "Error al guardar, intente nuevamente",
          });
        })
        .finally(() => {
          this.ratingLoading = false;
        });
    },
    payTicket() {
      this.$refs["modal"].show();
      redirectGatewayPayment({
        http: this.$http,
        ticket: {
          _id: this.ticket._id,
          payment: "Webpay",
        },
      }).catch((err) => {
        alert("Error al cargar el pago");
        this.$refs["modal"].hide();
        console.error(err);
      });
    },
    payTicketKlap() {
      this.$refs["modal"].show();
      redirectGatewayPayment({
        http: this.$http,
        ticket: {
          _id: this.ticket._id,
          payment: "Klap",
        },
      }).catch((err) => {
        alert("Error al cargar el pago");
        this.$refs["modal"].hide();
        console.error(err);
      });
    },
    payTicketMercadoPago() {
      this.$refs["modal"].show();
      const business =
        this.$route.params.name ||
        this.ticket.webName ||
        this.ticket.webDomain ||
        this.ticket.b ||
        this.info.webName ||
        this.info.webDomain ||
        "";

      redirectGatewayPayment({
        http: this.$http,
        business,
        ticket: {
          _id: this.ticket._id,
          payment: "MercadoPago",
          business,
        },
      }).catch((err) => {
        this.$swal({
          title: "Error al cargar el pago",
        });
        this.$refs["modal"].hide();
        console.error(err);
      });
    },
    cutEmail() {
      let email = this.ticket.email;
      let a = email.indexOf("@");
      let r = email.slice(a, a - 3);
      return email.replace(r, "****");
    },
    getContent(content) {
      return content
        .replaceAll("\n", "<br>")
        .replaceAll(
          '<a href="',
          '<a class="font-weight-bold" target="_blank" href="'
        );
    },
    getAproxHour() {
      let time = parseInt(this.settings.deliveryTime);
      let hour = this.$moment(this.ticket.hour, "HH:mm");
      if (!time || this.ticket.type == "Retiro")
        return `${hour.format("HH:mm")}`;
      return `${hour.format("HH:mm")} - ${hour
        .add(time, "minutes")
        .format("HH:mm")}`;
    },
    goCart() {
      this.$cookies.remove("ticketId");
      if (this.settings.style == "delivery")
        this.$router.push(this.getLink("menu"));
      else this.$router.push(this.getLink("productos"));
    },
    // suscribe() {
    //   if (!("Notification" in window)) {
    //     return alert(
    //       "Este navegador no es compatible con las notificaciones de escritorio"
    //     );
    //   }
    //   this.nstatus = Notification.permission;
    //   Notification.requestPermission(() => {
    //     this.nstatus = Notification.permission;
    //     navigator.serviceWorker
    //       .register("/worker.js", {
    //         scope: "/",
    //       })
    //       .then((register) => {
    //         // console.log(register);
    //         return register.pushManager.subscribe({
    //           userVisibleOnly: true,
    //           applicationServerKey: this.$store.state.PVK,
    //         });
    //       })
    //       .then((suscription) => {
    //         this.nstatus = Notification.permission;
    //         return this.$http.post(`ticket/${this.tid}/suscribe`, {
    //           s: JSON.stringify(suscription),
    //         });
    //       })
    //       .then(() => {
    //         this.load();
    //       })
    //       .catch(() => {
    //         alert("Error intente nuevamente");
    //       });
    //   }).catch(() => {
    //     alert("Error intente nuevamente");
    //   });
    // },
    load() {
      this.showReload = false;
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = null;

      this.$http
        .get("ticket/" + this.tid)
        .then((res) => {
          if (!this.pageActive) return;
          this.ticket = res.data.data;
          this.ratingSaved = !!(
            this.ticket.rating &&
            (this.ticket.rating.note || this.ticket.rating.comment)
          );
          if (this.ratingSaved) {
            this.rating = this.ticket.rating.note || 0;
            this.comment = this.ticket.rating.comment || "";
          }

          if (
            this.ticket.payment == "Klap" &&
            this.ticket.paymentStatus != "ok"
          ) {
            //this.payTicketKlap();
          }

          this.$http.get(res.data.data.b).then((res2) => {
            if (this.pageActive) this.$store.commit("setInfo", res2.data.data);
          }).catch(() => {});
          if (this.ticket.status == "Pendiente") {
            this.refreshTimer = setTimeout(() => {
              this.refreshTimer = null;
              if (this.pageActive) this.load();
            }, 60000);
          }

          this.loading = false;
        })
        .catch(() => {
          if (this.pageActive) this.showReload = true;
        });
    },
  },
  watch: {
    info() {
      document.title = `Ticket - ${this.info.custom_name || this.info.name}`;
    },
  },
};
</script>
